// src/pages/ClinicEmployees.js
import { useState, useEffect, useContext } from "react";
import { Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import useLanguage from "../../utils/useLanguage";
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import addIcon from "../../assets/add_icon.png";
import searchIcon from "../../assets/search_icon.png";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import BackToDashboard from "../../components/BackToDashboard";
import { DataContext } from "../../utils/DataContext";
import Loader from "../../components/Loader";
import { UserAuth } from "../../utils/AuthContext";
import PageWrapper from "../../components/PageWrapper";

export default function Employees() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  // const [networkError, setNetworkError] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModal, setUserModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userFields, setUserFields] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalRecords, setUsersTotalRecords] = useState(0);

  const { selectedSupplier, loading, setLoading } = useContext(DataContext);
  const { userData } = useContext(UserAuth);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const organization = localStorage.getItem("username");
  const organizationId = localStorage.getItem("organizationId");

  useEffect(() => {
    const getAllEmployees = async () => await getEmployees();

    getAllEmployees();
  }, [usersCurrentPage]);

  // users options
  const options = [
    { name: "Doctor", title: "Doctor" },
    { name: "Healer", title: "Healer" },
    { name: "Receptionist", title: "Receptionist" },
  ];

  const getEmployees = async (page) => {
    const token = localStorage.getItem("token");

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": organizationId,
          },
          params: {
            page: page || usersCurrentPage,
            limit: 5,
            search: searchTerm,
          },
        }
      );

      if (response.data && response.data.users) {
        // Transform the data to match your existing structure
        const transformedUsers = response.data.users.map((user) => ({
          id: user.id,
          name: user.username,
          email: user.email,
          phone: user.phoneNumber,
          role: user.role,
          specialty: user.specialty,
          isBlocked: user.isBlocked,
          organization: user.organization?.organizationName || organization,
        }));

        setUsers(transformedUsers);
        setFilteredUsers(transformedUsers);
        setUsersTotalPages(response.data.totalPages);
        setUsersTotalRecords(response.data.totalUsers);
      } else {
        console.error("Failed to get the users");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Failed to get the users", error);
    }
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setUserFields({
      userName: user.name,
      userEmail: user.email,
      userPhoneNumber: user.phone,
      organizationName: organization || "No organization",
      isBlocked: user.isBlocked,
      role: user.role,
      specialty: user.specialty,
    });
    setIsEditing(false);
    setUserModal(true);
  };

  const handleInputChange = (e, fieldName) => {
    const value = fieldName === "isBlocked" ? e.target.checked : e.target.value;
    setUserFields({
      ...userFields,
      [fieldName]: value,
    });
  };

  const handleSaveUser = async (user) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/editUser`,
        {
          id: user.id,
          username: userFields.userName,
          email: userFields.userEmail,
          phoneNumber: userFields.userPhoneNumber,
          role: userFields.role,
          specialty: userFields.role === "Doctor" ? userFields.specialty : null,
          isBlocked: userFields.isBlocked,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": organizationId,
          },
        }
      );

      if (response.status === 200) {
        setUserModal(false);
      } else {
        console.error("Failed to update user:", response.data.message);
      }
      setLoadingModal(false);
    } catch (error) {
      console.error("Error updating user:", error);
      setLoadingModal(false);
    } finally {
      await getEmployees();
    }
  };

  const handleCancel = () => {
    setRemoveUserModal(false);
    setUserModal(false);
    setSelectedUser(null);
    setLoadingModal(false);
  };

  const openRemoveUserModal = (user) => {
    setSelectedUser(user);
    setRemoveUserModal(true);
  };

  const [showRemoveUserModal, setRemoveUserModal] = useState(false);
  const handleDeleteUser = async () => {
    const token = localStorage.getItem("token");
    setLoadingModal(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/deleteUser/${selectedUser.id}`,
        {
          data: { userId: selectedUser.id },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status == 204) {
        setRemoveUserModal(false);
        setUserModal(false);
        setRemoveUserModal(false);
      } else {
        console.error("Failed to delete user:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setLoadingModal(false);
      setUserModal(false);
      await getEmployees();
    }
  };

  const [message, setMessage] = useState("");
  const [addUserModal, setAddUserModal] = useState(false);
  const [newUserFields, setNewUserFields] = useState({
    userName: "",
    userEmail: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    isBlocked: false,
    role: "",
    specialty: "",
  });

  const openAddUserModal = () => {
    setAddUserModal(true);
    setNewUserFields({
      userName: "",
      userEmail: "",
      password: "",
      confirmPassword: "",
      isSuperUser: false,
    });
  };

  const handleNewUserInputChange = (e, fieldName) => {
    const value = fieldName === "isBlocked" ? e.target.checked : e.target.value;
    setNewUserFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  // create user function
  // Update the handleCreateUser function
  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      // Determine the language based on currentDirection
      const language = currentDirection === "rtl" ? "ar" : "en";

      // Empty fields validation
      if (
        !newUserFields.userEmail ||
        !newUserFields.userName ||
        !newUserFields.password ||
        !newUserFields.confirmPassword ||
        !newUserFields.phoneNumber ||
        !newUserFields.role
      ) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      // Email validation regex
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (newUserFields.userEmail) {
        if (!emailPattern.test(newUserFields.userEmail)) {
          setError(true);
          setErrorMessage(t("invalidEmail"));
          return;
        }
      }

      // Password match validation
      if (newUserFields.password !== newUserFields.confirmPassword) {
        setError(true);
        setErrorMessage("Passwords do not match");
        return;
      }

      setLoadingModal(true);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createUser`,
        {
          email: newUserFields.userEmail,
          username: newUserFields.userName,
          password: newUserFields.password,
          phoneNumber: newUserFields.phoneNumber,
          isBlocked: newUserFields.isBlocked,
          role: newUserFields.role,
          specialty: newUserFields.specialty || "",
          isOrganization: true,
          organizationId: Number(organizationId),
          language: language, // Added language parameter
        }
      );

      if (response.data.message === "Verification email sent") {
        setError(false);
        setErrorMessage("");
        setAddUserModal(false);
        setMessage(t("verificationEmailSent")); // Show success message
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorKey = error.response.data.error;
        switch (errorKey) {
          case "emailAlreadyInUse":
            setError(true);
            setErrorMessage(t("emailAlreadyInUse"));
            break;
          default:
            setError(true);
            setErrorMessage(t("unexpectedError"));
            break;
        }
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage(t("networkError"));
      }
    } finally {
      setLoadingModal(false);
      await getEmployees();
    }
  };

  const handleCancelAddUser = () => {
    setError(false);
    setErrorMessage("");
    setAddUserModal(false);
  };

  const handleSearch = async () => {
    setUsersCurrentPage(1);
    await getEmployees(1);
  };

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}
          title={t("organizations")}
          withBackButton
      >


        <div className="users-title-section">
          <h4 className="page--title">{t("users", { defaultValue: "users" })}</h4>

          {!loading && userData.userType === "organization" ? (
            <CustomButton
              title={t("createUser")}
              variant="blank"
              icon={addIcon}
              iconSize={20}
              onClick={openAddUserModal}
            />
          ) : null}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="o-row o-row--vertical-align_center o-row--gap-size_300">
              <CustomInput
                name="userName"
                value={searchTerm}
                placeholder={searchTerm ? "" : t("search")}
                onChange={(e) => setSearchTerm(e.target.value)}
                flex
                thick
                resetSearch={() => setSearchTerm("")}
              />
              <CustomButton
                title={t("search")}
                icon={searchIcon}
                iconSize={15}
                onClick={handleSearch}
              />
            </div>
            {filteredUsers.length === 0 ? (
              <p>{t("noUsers")}</p>
            ) : (
              <>
                <ul
                  className="users-list"
                  style={{ direction: currentDirection }}
                >
                  {filteredUsers.map((user) => (
                    <li
                      className="user-list-item"
                      key={user.id}
                      onClick={() => openUserModal(user)}
                      style={{ cursor: "pointer" }}
                    >
                      <p className="user-username">
                        {user.name || "No Name Available"}{" "}
                        {/* Changed from username to name */}
                      </p>
                      {user.email && (
                        <Typography variant="subtitle1" color="textSecondary">
                          {user.email}
                        </Typography>
                      )}
                      <Typography variant="subtitle2" color="textSecondary">
                        {user.role} {/* Display role */}
                      </Typography>
                    </li>
                  ))}
                </ul>

                <section className="pagination">
                  <div className="pagination-buttons">
                    <CustomButton
                      variant="blank"
                      title={t("previous")}
                      onClick={() => setUsersCurrentPage(usersCurrentPage - 1)}
                      disabled={usersCurrentPage === 1}
                      icon={
                        currentDirection === "ltr" ? previousIcon : nextIcon
                      }
                      iconSize={15}
                    />
                    <span className="pagination-location">
                      {t("page")}: {usersCurrentPage} - {usersTotalPages}
                    </span>
                    <CustomButton
                      title={t("next")}
                      variant="blank"
                      onClick={() => setUsersCurrentPage(usersCurrentPage + 1)}
                      disabled={usersCurrentPage === usersTotalPages}
                      icon={
                        currentDirection === "ltr" ? nextIcon : previousIcon
                      }
                      iconSize={15}
                      reverseIcon
                    />
                  </div>

                  <div className="pagination-buttons">
                    <span className="pagination-location">
                      {t("totalNumber")}: {usersTotalRecords}
                    </span>
                  </div>
                </section>
              </>
            )}
          </>
        )}

        {userModal && (
          <Modal
            title={isEditing ? t("editUser") : t("user")}
            onClose={handleCancel}
            withDropdown
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        title={t("confirm")}
                        onClick={() => handleSaveUser(selectedUser)}
                        icon={saveIcon}
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon={editIcon}
                        iconSize={18}
                      />
                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        icon={deleteIcon}
                        iconSize={18}
                        onClick={() => openRemoveUserModal(selectedUser)}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("username")}: </span>
                      <CustomInput
                        name="userName"
                        value={userFields.userName || ""}
                        onChange={(e) => handleInputChange(e, "userName")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("username")}:</p>
                      <p>{selectedUser?.name}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div className="view-user-entity">
                  <p>{t("email")}:</p>
                  <p>{selectedUser?.email}</p>
                </div>
              </section>
              <section>
                <div>
                  <div className="view-user-entity">
                    <p>{t("organization")}:</p>
                    <p>{organization || "Individual user"}</p>
                  </div>
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("phoneNumber")}: </span>
                      <CustomInput
                        name="userPhoneNumber"
                        value={userFields.userPhoneNumber || ""}
                        onChange={(e) =>
                          handleInputChange(e, "userPhoneNumber")
                        }
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("phoneNumber")}:</p>
                      <p>{selectedUser?.phone}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                {isEditing ? (
                  <div className="edit-user-entity">
                    <div className="user-row-section">
                      <CustomCheckbox
                        checked={userFields?.isBlocked}
                        onChange={(e) => handleInputChange(e, "isBlocked")}
                        color="primary"
                      />
                      <label>{t("isBlocked")}</label>
                    </div>
                  </div>
                ) : (
                  <div className="view-user-entity">
                    <p>{t("theUseIsBlocked")}:</p>
                    <p>{selectedUser?.isBlocked ? t("yes") : t("no")}</p>{" "}
                  </div>
                )}
              </section>
            </div>
          </Modal>
        )}

        {showRemoveUserModal && (
          <Modal
            title={t("deleteUser")}
            onClose={() => setRemoveUserModal(false)}
            withDropdown
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="delete-user-actions">
                  <CustomButton
                    onClick={handleDeleteUser}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setRemoveUserModal(false)}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <div className="delete-modal-body">
              <h3>{t("areYouSureYouWantToDeleteThisUser")}</h3>
              <p>{selectedUser?.name || selectedUser?.email}</p>
            </div>
          </Modal>
        )}

        {/* Add User Modal */}
        {addUserModal && (
          <Modal
            title={t("createUser")}
            onClose={handleCancelAddUser}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleCreateUser}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancelAddUser}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <form onSubmit={handleCreateUser}>
              <div>
                {error && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ErrorBox>{errorMessage}</ErrorBox>
                  </div>
                )}
                <div className="edit-user-entity">
                  <span>{t("role")}: </span>
                  <Dropdown
                    options={options}
                    setSelected={(item) => {
                      setNewUserFields((prev) => {
                        return { ...prev, role: item.name };
                      });
                    }}
                    flex
                    thick
                    selected={{ title: newUserFields?.role }}
                    value={newUserFields?.role}
                  />
                </div>

                {newUserFields.role == "Doctor" && (
                  <div className="edit-user-entity">
                    <span>{t("medicalSpecialty")}: </span>
                    <CustomInput
                      name="specialty"
                      value={newUserFields.specialty}
                      onChange={(e) => handleNewUserInputChange(e, "specialty")}
                    />
                  </div>
                )}

                <div className="edit-user-entity">
                  <span>{t("username")}: </span>
                  <CustomInput
                    name="userName"
                    value={newUserFields.userName}
                    onChange={(e) => handleNewUserInputChange(e, "userName")}
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("email")}: </span>
                  <CustomInput
                    name="userEmail"
                    type="email"
                    value={newUserFields.userEmail}
                    onChange={(e) => handleNewUserInputChange(e, "userEmail")}
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("organization")}: </span>
                  <CustomInput
                    name="organizationName"
                    value={organization}
                    disabled
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("phoneNumber")}: </span>
                  <CustomInput
                    name="userName"
                    value={newUserFields.phoneNumber}
                    onChange={(e) => handleNewUserInputChange(e, "phoneNumber")}
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("password")}: </span>
                  <CustomInput
                    name="password"
                    type="password"
                    value={newUserFields.password}
                    onChange={(e) => handleNewUserInputChange(e, "password")}
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("confirmPassword")}: </span>
                  <CustomInput
                    name="confirmPassword"
                    type="password"
                    value={newUserFields.confirmPassword}
                    onChange={(e) =>
                      handleNewUserInputChange(e, "confirmPassword")
                    }
                    required
                  />
                </div>

                <div className="edit-user-entity">
                  <div className="user-row-section">
                    <CustomCheckbox
                      checked={newUserFields.isBlocked}
                      onChange={(e) => handleNewUserInputChange(e, "isBlocked")}
                      color="primary"
                    />
                    <label>{t("isBlocked")}</label>
                  </div>
                </div>
              </div>
            </form>
          </Modal>
        )}
      </PageWrapper>
    </div>
  );
}
